export const LEAVE_APPROVAL_ROLES = [
  { value: "MANAGER", label: "Reporting Manager" },
  { value: "EMPLOYEE", label: "Employee / Self" },
  { value: "reporting_manager", label: "Reporting Manager" },
  { value: "manager", label: "Manager" },
  { value: "hr", label: "HR" },
  { value: "management", label: "Management" },
  { value: "admin", label: "Admin" },
];

const normalizeWorkflowSteps = (steps, fallback) => {
  const normalized = Array.isArray(steps) ? steps : [];
  if (normalized.length === 0) return fallback;
  return normalized.map((step) => ({
    role: step.role || "reporting_manager",
    slaDays: Number(step.slaDays) || 3,
    notifyOnly: !!step.notifyOnly,
  }));
};

export const defaultLeaveGroupWorkflow = () => ({
  approvalMethod: "workflow",
  needWorkflowSteps: false,
  reminderDays: 7,
  workflowParallelApproval: false,
  workflowEscalationAfterDays: 3,
  workflowAutoApprovalIfNoAction: false,
  workflowRejectionWithReason: true,
  workflowReinitiationAllowed: true,
  workflowSteps: [{ role: "MANAGER", slaDays: 3, notifyOnly: false }],
  cancellationWorkflowSteps: [{ role: "MANAGER", slaDays: 3, notifyOnly: false }],
});

export const isSeparateWorkflowEnabled = (approval = {}) =>
  approval.enableSeparateWorkflow === true ||
  approval.enableSeparateWorkflow === "Yes";

export const defaultApprovalWorkflow = () => ({
  enableSeparateWorkflow: "No",
  ...defaultLeaveGroupWorkflow(),
});

export function normalizeLeaveTypeApprovalWorkflow(raw = {}) {
  const base = defaultApprovalWorkflow();
  const merged = { ...base, ...raw };
  if (merged.approvalMethod === "on_instance") {
    merged.approvalMethod = "auto_approved";
    merged.needWorkflowSteps = false;
  }
  merged.enableSeparateWorkflow = isSeparateWorkflowEnabled(merged) ? "Yes" : "No";
  merged.workflowSteps = normalizeWorkflowSteps(merged.workflowSteps, base.workflowSteps);
  merged.cancellationWorkflowSteps = normalizeWorkflowSteps(
    merged.cancellationWorkflowSteps,
    base.cancellationWorkflowSteps
  );
  return merged;
}

export function normalizeLeaveGroupWorkflow(raw = {}) {
  const base = defaultLeaveGroupWorkflow();
  const merged = { ...base, ...raw };
  if (merged.approvalMethod === "on_instance") {
    merged.approvalMethod = "auto_approved";
    merged.needWorkflowSteps = false;
  }
  merged.workflowSteps = normalizeWorkflowSteps(merged.workflowSteps, base.workflowSteps);
  merged.cancellationWorkflowSteps = normalizeWorkflowSteps(
    merged.cancellationWorkflowSteps,
    base.cancellationWorkflowSteps
  );
  return merged;
}

export function normalizeApprovalWorkflow(raw = {}) {
  return normalizeLeaveTypeApprovalWorkflow(raw);
}

export function mapLeaveRequestApiToInsight(row) {
  if (!row) return null;
  const requestType = String(row.requestType || "leave").toLowerCase();
  const cancelledAfterApproval = !!row.cancelledAfterApproval;
  const deskStatus = (() => {
    const raw = String(row.status || "").toLowerCase();
    if (raw === "pending" || raw === "cancellation_pending") return "Pending";
    if (raw === "approved") return "Approved";
    if (raw === "rejected") return "Rejected";
    if (raw === "cancelled") {
      // Original leave stays on Approved after a completed cancel so tracking
      // remains on the same card. Pending cancel children stay their own row.
      if (requestType === "cancellation") return "Approved";
      if (cancelledAfterApproval) return "Approved";
      return "Undo";
    }
    return row.status || "Pending";
  })();
  const snap = row.workflowSnapshot || {};
  const defined =
    (Array.isArray(snap.approvalSteps) && snap.approvalSteps) ||
    (Array.isArray(snap.steps) && snap.steps) ||
    (Array.isArray(snap.workflowSteps) && snap.workflowSteps) ||
    [];
  const approvals = Array.isArray(row.approvals) ? row.approvals : [];
  const byOrder = new Map(approvals.map((a) => [Number(a.stepOrder ?? a.order), a]));
  const current = Number(row.currentStep) || 0;
  const pendingPerson =
    row.pendingAssigneeDisplay ||
    (row.pendingAt &&
    String(row.pendingAt) !== String(row.pendingLabel || "") &&
    String(row.pendingAt) !== String(row.pendingRole || "")
      ? row.pendingAt
      : null);
  const workflowTrail =
    defined.length > 0
      ? defined.map((step, idx) => {
          const order = Number(step.order ?? step.stepOrder ?? idx + 1);
          const a = byOrder.get(order);
          let status = "Pending";
          if (a?.action === "approved" || a?.status === "approved") status = "Approved";
          else if (a?.action === "rejected" || a?.status === "rejected") status = "Rejected";
          else if (
            current === order &&
            (row.status === "pending" ||
              row.status === "cancellation_pending" ||
              deskStatus === "Pending")
          )
            status = "Current";
          const actedName = a?.actorName || a?.approverName || null;
          const isRm =
            /manager/i.test(String(step.role || "")) ||
            String(step.role || "").toUpperCase() === "MANAGER";
          const actor =
            actedName ||
            (status === "Current" || status === "Pending"
              ? isRm
                ? pendingPerson || roleLabel(step.role)
                : roleLabel(step.role)
              : roleLabel(step.role));
          return {
            status,
            role: step.role,
            actor,
            displayText: actor,
            actedAt: a?.actedAt || null,
            reason: a?.comments || a?.reason || "",
            stepOrder: order,
          };
        })
      : approvals.map((a, idx) => ({
          status:
            a.action === "approved" || a.status === "approved"
              ? "Approved"
              : a.action === "rejected" || a.status === "rejected"
                ? "Rejected"
                : "Pending",
          role: a.role,
          actor: a.actorName || a.approverName || roleLabel(a.role),
          displayText: a.actorName || a.approverName || roleLabel(a.role),
          actedAt: a.actedAt || null,
          reason: a.comments || "",
          stepOrder: a.stepOrder ?? idx + 1,
        }));

  const pendingWith =
    pendingPerson ||
    row.pendingAt ||
    row.pendingLabel ||
    roleLabel(row.pendingRole) ||
    "";

  return {
    id: `leave_req_${row.id}`,
    requestId: `leave_req_${row.id}`,
    leaveRequestId: row.id,
    action:
      requestType === "cancellation" ? "Leave cancellation" : "Leave",
    actionLabel:
      requestType === "cancellation" ? "Leave cancellation" : "Leave",
    employeeCode: row.employeeCode || "",
    employeeName: row.employeeName || "",
    employeeId: row.employeeId,
    status: deskStatus,
    pendingAt: pendingWith || "-",
    pendingWith,
    pendingRole: row.pendingRole || null,
    pendingLabel: row.pendingLabel || roleLabel(row.pendingRole) || null,
    pendingAssigneeDisplay: pendingPerson || null,
    canAct: row.canAct === true,
    canRequestCancellation: row.canRequestCancellation === true,
    requestType,
    parentLeaveRequestId: row.parentLeaveRequestId || null,
    cancelledAfterApproval,
    openCancellationRequestId: row.openCancellationRequestId || null,
    apiStatus: row.status || null,
    submittedAt: row.submittedAt
      ? new Date(row.submittedAt).toLocaleString()
      : "",
    formData: row.formData || {},
    approvals,
    workflowSnapshot: snap,
    currentStep: row.currentStep,
    totalSteps: row.totalSteps,
    workflowTrail,
    workflowSteps: workflowTrail,
  };
}

export function roleLabel(role) {
  if (!role) return "";
  const found = LEAVE_APPROVAL_ROLES.find(
    (r) => String(r.value).toLowerCase() === String(role).toLowerCase()
  );
  return found?.label || role;
}
